import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Bot, User, Zap, Flame, Wind, Dumbbell } from "lucide-react";

const QUICK = [
  { label: "Circuito brucia-grassi 20 min", focus: "brucia_grassi", duration: 20, icon: Flame },
  { label: "Gambe intense 30 min", focus: "gambe", duration: 30, icon: Dumbbell },
  { label: "Fiato e cardio 25 min", focus: "cardio", duration: 25, icon: Wind },
  { label: "Full body 40 min", focus: "full_body", duration: 40, icon: Zap },
];

export default function Coach() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Ciao. Sono il tuo Coach IA. Posso generarti una scheda personalizzata (usa i pulsanti qui sotto) o rispondere a dubbi su tecnica, alimentazione, recupero. Cosa vuoi fare oggi?",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const generateWorkout = async (focus, duration) => {
    setGenerating(true);
    toast.info("Coach al lavoro... genero la scheda");
    try {
      const r = await api.post("/coach/generate", {
        focus,
        duration_min: duration,
        equipment: ["dumbbells", "kettlebell", "bands", "bodyweight"],
        notes: "Obiettivo: perdita peso, gambe forti, migliorare il fiato",
      });
      toast.success(`Scheda "${r.data.title}" creata`);
      navigate(`/workouts/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione");
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input.trim() };
    const history = [...messages, userMsg];
    setMessages([...history, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const token = localStorage.getItem("fc_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg.content, history: messages }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          try {
            const obj = JSON.parse(data);
            if (obj.delta) {
              acc += obj.delta;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
              });
            } else if (obj.error) {
              toast.error("Errore coach");
            }
          } catch {}
        }
      }
    } catch (err) {
      toast.error("Errore rete coach");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-2 flex items-center gap-2">
        <Bot className="w-3 h-3" /> Terminale Coach
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Coach <span className="text-[#FF3B30]">IA</span>
      </h1>

      {/* Quick actions */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-3">Genera scheda al volo</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK.map((q) => (
            <button
              key={q.label}
              disabled={generating}
              onClick={() => generateWorkout(q.focus, q.duration)}
              data-testid={`quick-${q.focus}`}
              className="border border-[#2A2A35] hover:border-[#FF3B30] p-4 text-left transition-all group disabled:opacity-40"
            >
              <q.icon className="w-5 h-5 text-[#FF3B30] mb-2" strokeWidth={2.5} />
              <div className="text-sm font-bold uppercase leading-tight group-hover:text-[#FF3B30]">
                {q.label}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Genera con IA
              </div>
            </button>
          ))}
        </div>
        {generating && (
          <div className="mt-3 text-xs uppercase tracking-widest text-[#E2FF31]" data-testid="generating-indicator">
            [ ... genero la scheda ... ]
          </div>
        )}
      </div>

      {/* Chat */}
      <div className="border border-[#2A2A35] bg-[#141419]/50">
        <div className="border-b border-[#2A2A35] px-4 py-2 flex items-center gap-2 text-xs font-mono-alt">
          <span className="w-2 h-2 rounded-full bg-[#E2FF31]" />
          <span className="text-[#A0A0AB] uppercase tracking-widest">coach.io — chat live</span>
        </div>
        <div ref={listRef} className="max-h-[500px] min-h-[300px] overflow-y-auto p-6 space-y-4" data-testid="chat-list">
          {messages.map((m, i) => (
            <div key={i} className="flex gap-3">
              <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center border ${
                m.role === "assistant" ? "border-[#E2FF31] text-[#E2FF31]" : "border-[#FF3B30] text-[#FF3B30]"
              }`}>
                {m.role === "assistant" ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mb-1">
                  {m.role === "assistant" ? "COACH" : "TU"}
                </div>
                <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                  {m.content || (streaming && i === messages.length - 1 && <span className="text-[#A0A0AB]">...</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#2A2A35] p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            disabled={streaming}
            data-testid="chat-input"
            placeholder="Scrivi una domanda..."
            className="flex-1 bg-[#0B0B0F] border border-[#2A2A35] px-4 py-3 focus:border-[#FF3B30] focus:outline-none text-sm"
          />
          <button
            onClick={send}
            disabled={streaming || !input.trim()}
            data-testid="chat-send"
            className="bg-[#FF3B30] text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#e02e24] transition-all disabled:opacity-40 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
